import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/config';
import { requestFCMToken, onForegroundMessage } from '../firebase/messaging';

/**
 * useFCMToken — manages Firebase Cloud Messaging registration for this device.
 *
 * Responsibilities:
 *  - Tracks browser notification permission state
 *  - On permission grant, fetches the FCM token and saves it to
 *    users/{uid}.fcmTokens (arrayUnion — survives multi-device households)
 *  - Handles foreground messages by showing a Notification directly
 *    (background messages are handled by firebase-messaging-sw.js)
 *  - Re-registers the token on every app open so stale tokens are refreshed
 *
 * Returns { permission, requestPermission, tokenReady }
 *
 * @param {string|null} userId — Firebase Auth UID of the current user
 */
export function useFCMToken(userId) {
  const [permission, setPermission] = useState(() => {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
  });
  const [tokenReady, setTokenReady] = useState(false);
  const unsubRef = useRef(null);

  // Persist the FCM token to Firestore for this user.
  // arrayUnion ensures we keep tokens from all their devices without duplication.
  const saveToken = useCallback(async (uid) => {
    const token = await requestFCMToken();
    if (!token || !uid) return;
    try {
      await updateDoc(doc(db, 'users', uid), {
        fcmTokens: arrayUnion(token),
      });
      setTokenReady(true);
    } catch {
      // Non-fatal — token will be retried on next session
    }
  }, []);

  // Auto-register when permission is already granted (e.g. returning user)
  useEffect(() => {
    if (permission === 'granted' && userId) {
      saveToken(userId);
    }
  }, [permission, userId, saveToken]);

  // Foreground message handler — shows a notification when the tab is active.
  // Firebase only fires onMessage() when the page is in the foreground;
  // background delivery is handled by the service worker.
  useEffect(() => {
    let active = true;

    onForegroundMessage((payload) => {
      if (!active) return;
      if (!('Notification' in window) || Notification.permission !== 'granted') return;

      const title = payload.notification?.title || 'Baby Tracker';
      const body  = payload.notification?.body  || '';

      new Notification(title, { // eslint-disable-line no-new
        body,
        icon:  '/logo192.png',
        badge: '/logo192.png',
        tag:   payload.data?.tag || 'baby-tracker',
      });
    }).then((unsub) => {
      if (active) unsubRef.current = unsub;
    });

    return () => {
      active = false;
      unsubRef.current?.();
    };
  }, []);

  /**
   * requestPermission — asks the browser for notification permission,
   * then registers the FCM token if granted.
   * Returns the new permission string: 'granted' | 'denied' | 'default'.
   */
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      setPermission('unsupported');
      return 'unsupported';
    }
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === 'granted' && userId) {
      await saveToken(userId);
    }
    return result;
  }, [userId, saveToken]);

  return { permission, requestPermission, tokenReady };
}
